import { Box, Button, Chip, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { BreefTrackingSection } from "../breefTracking/BreefTrackingList";
import VideoConversions from "../setting/VideoConversions";
import Layout from "../../components/Layout";
import PageHeader from "../../components/ui/PageHeader";
import { getQueuedMessages, getQueueOverview, QueuedMessage, QueueOverview, retryFailedMessages, retryMessage } from "../../service/QueueService";
import { showSuccessToast } from "../../utils/Toast";
import { orangeButton, outlinedButton, StyledTableCell, StyledTableRow, TableWrapper } from "../logs/logStyles";

const REFRESH_MS = 10000;
const statusColor = (status: QueuedMessage["status"]) => (status === "sent" ? "success" : status === "failed" ? "error" : "warning");
const minutes = (seconds: number) => (seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`);

/**
 * Køer (superadmin): the three queues the worker runs - the messages (login codes, reminders, results), the
 * invitations and the video conversions - with what is waiting, what went out today, what failed and why, and
 * "try again" for a failed message or all of them. Refreshes every ten seconds.
 */
export default function Queues() {
  const [t] = useTranslation("global");
  const [overview, setOverview] = useState<QueueOverview | null>(null);
  const [messages, setMessages] = useState<QueuedMessage[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [busy, setBusy] = useState(false);
  // Which queue is open: ?tab=messages|invites|videos (the cards switch it too)
  const [params, setParams] = useSearchParams();
  const tab = (["messages", "invites", "videos"].includes(params.get("tab") || "") ? params.get("tab") : "messages") as "messages" | "invites" | "videos";
  const openTab = (value: string) => setParams({ tab: value });

  const load = () => {
    getQueueOverview().then(setOverview);
    getQueuedMessages(filter).then((data) => setMessages(data?.results || []));
  };
  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const retryOne = async (id: string) => {
    setBusy(true);
    if (await retryMessage(id)) showSuccessToast(t("queues.retried"), false);
    setBusy(false);
    load();
  };

  const retryAll = async () => {
    setBusy(true);
    const count = await retryFailedMessages();
    setBusy(false);
    if (count !== null) showSuccessToast(t("queues.retriedAll", { count }), false);
    load();
  };

  const counts = (row: { queued: number; sending?: number; processing?: number; sent24h?: number; done24h?: number; failed: number; oldestQueuedSeconds?: number; delivered24h?: number; undelivered24h?: number }) =>
    t("queues.counts", { queued: row.queued, sending: row.sending ?? row.processing ?? 0, sent: row.sent24h ?? row.done24h ?? 0, failed: row.failed }) +
    (row.delivered24h !== undefined ? ` · ${t("queues.delivered", { delivered: row.delivered24h, undelivered: row.undelivered24h ?? 0 })}` : "") +
    (row.oldestQueuedSeconds ? ` · ${t("queues.oldest", { age: minutes(row.oldestQueuedSeconds) })}` : "");

  return (
    <Layout>
      <PageHeader title={t("queues.title")} description={t("queues.intro")} />

      {overview && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
          {(["messages", "invites", "videos"] as const).map((key) => (
            <Paper key={key} sx={{ p: 2, cursor: "pointer", borderColor: tab === key ? "#EB6608" : undefined }} variant="outlined" data-testid={`queue-${key}`} onClick={() => openTab(key)}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t(`queues.${key}`)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {counts(overview[key])}
              </Typography>
              {overview[key].failed > 0 && (
                <Chip label={t("queues.failedCount", { count: overview[key].failed })} color="error" size="small" sx={{ mt: 1 }} />
              )}
            </Paper>
          ))}
        </Box>
      )}

      <Tabs value={tab} onChange={(_event, value) => openTab(value)} sx={{ mb: 2 }}>
        <Tab value="messages" label={t("queues.messages")} />
        <Tab value="invites" label={t("queues.invites")} />
        <Tab value="videos" label={t("queues.videos")} />
      </Tabs>

      {tab === "invites" && <BreefTrackingSection />}
      {tab === "videos" && <VideoConversions />}

      {tab === "messages" && (
      <>
      <Box sx={{ width: "100%", mb: 2, display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_event, value) => setFilter(value ?? "")}>
          <ToggleButton value="">{t("queues.all")}</ToggleButton>
          <ToggleButton value="queued">{t("queues.statuses.queued")}</ToggleButton>
          <ToggleButton value="sent">{t("queues.statuses.sent")}</ToggleButton>
          <ToggleButton value="failed">{t("queues.statuses.failed")}</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" sx={orangeButton} disabled={busy || !overview?.messages.failed} onClick={retryAll}>
          {t("queues.retryAll")}
        </Button>
      </Box>

      <TableWrapper component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("queues.time")}</StyledTableCell>
              <StyledTableCell>{t("queues.kind")}</StyledTableCell>
              <StyledTableCell>{t("queues.channel")}</StyledTableCell>
              <StyledTableCell>{t("queues.to")}</StyledTableCell>
              <StyledTableCell>{t("queues.priority")}</StyledTableCell>
              <StyledTableCell>{t("queues.status")}</StyledTableCell>
              <StyledTableCell>{t("queues.delivery")}</StyledTableCell>
              <StyledTableCell>{t("queues.attempts")}</StyledTableCell>
              <StyledTableCell>{t("queues.error")}</StyledTableCell>
              <StyledTableCell align="right">{t("queues.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map((message) => (
              <StyledTableRow key={message.id}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{new Date(message.createdAt).toLocaleString("da-DK")}</TableCell>
                <TableCell>{t(`queues.kinds.${message.kind}`, { defaultValue: message.kind })}</TableCell>
                <TableCell>{message.channel === "sms" ? "SMS" : "E-mail"}</TableCell>
                <TableCell>{message.to}</TableCell>
                <TableCell>{message.priority}</TableCell>
                <TableCell>
                  <Chip label={t(`queues.statuses.${message.status}`)} color={statusColor(message.status)} size="small" />
                  {message.status === "queued" && message.nextAttemptAt && new Date(message.nextAttemptAt) > new Date() && (
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {t("queues.nextTry", { time: new Date(message.nextAttemptAt).toLocaleTimeString("da-DK") })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {message.delivery?.status ? (
                    <Tooltip title={`${message.provider ? `${message.provider}: ` : ""}${message.delivery.detail || ""}${message.delivery.at ? ` (${new Date(message.delivery.at).toLocaleTimeString("da-DK")})` : ""}`}>
                      <Chip
                        label={t(`queues.deliveryStatuses.${message.delivery.status}`, { defaultValue: message.delivery.status })}
                        color={message.delivery.status === "delivered" ? "success" : ["failed", "expired"].includes(message.delivery.status) ? "error" : "default"}
                        size="small"
                        variant={message.delivery.status === "pending" ? "outlined" : "filled"}
                      />
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{message.attempts}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  {message.lastError ? (
                    <Tooltip title={message.lastError}>
                      <Typography variant="body2" noWrap>
                        {message.lastError}
                      </Typography>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {(message.status === "failed" || message.status === "queued") && (
                    <Button size="small" variant="outlined" sx={outlinedButton} disabled={busy} onClick={() => retryOne(message.id)}>
                      {t("queues.retry")}
                    </Button>
                  )}
                </TableCell>
              </StyledTableRow>
            ))}
            {!messages.length && (
              <TableRow>
                <TableCell colSpan={10}>{t("queues.none")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
      </>
      )}
    </Layout>
  );
}
